export enum UserRole {
  admin = 'admin',
  support = 'support',
  partner = 'partner',
  manager = 'manager',
  salesAgent = 'salesAgent',
  salesManager = 'salesManager',
  staff = 'staff',
  marketing = 'marketing',
  brandOwner = 'brandOwner',
  /**
   * vDrive deliveries manager (apps/deliveries). Client-side only until the
   * deliveries backend exists - no backend endpoint issues this role yet.
   */
  deliveries = 'deliveries',
}

/**
 * Everyone who works at vGrubs, as the chat means it.
 *
 * A colleague is a direct message; everyone else is a support thread - and
 * "everyone else" includes sales. They spent a while on this side, while the
 * backend was moving their app onto the internal chat, and that was reverted:
 * an agent is now an outside user again, answered in the support queue's own
 * Sales tab. Keep this list in step with `supportAudience` on the backend, or
 * the app opens the wrong kind of thread with somebody.
 */
export const INTERNAL_ROLES: readonly UserRole[] = [
  UserRole.admin,
  UserRole.support,
  UserRole.marketing,
];

export const isInternalRole = (role?: UserRole | string | null) =>
  !!role && (INTERNAL_ROLES as readonly string[]).includes(role);

export type User = {
  id: number;
  phoneNumber: string;
  email: string | null;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  achCustomerId: string | null;
  deleted: boolean;
  role: UserRole;
  completed: boolean;
  termsAccepted: boolean;
  emailVerified: boolean;
  notificationsEnabled: boolean;
  emailNotificationsEnabled: boolean;
  /**
   * Token of the MOST RECENTLY registered device, not necessarily this one -
   * a user can be signed in on several devices at once. Kept for backwards
   * compatibility; use `fcmTokens` to decide whether this device is registered.
   */
  fcmToken: string | null;
  /**
   * Every device the user is signed in on, newest first. Push goes to all of
   * them. Always present, `[]` when there is none - but typed optional because
   * older persisted `user` blobs in localStorage predate the field.
   */
  fcmTokens?: string[];
  createdAt: string;
  updatedAt: string;
};

export type SimpleUserEntity = Pick<
  User,
  'id' | 'firstName' | 'lastName' | 'role' | 'createdAt'
> & {
  phone: {
    nationalNumber: string;
    number: string;
  };
};
